import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { getHistory, cancelJob, cancelPremiumJob, cancelHairAnalysisJob, cancelMakeupAnalysisJob } from '../api/endpoints';
import { AuthImage } from '../components/AuthImage';
import { ProductPairThumbnail } from '../components/ProductPairThumbnail';
import Loading from '../components/Loading';
import { navigateTo } from '../utils/navigation';
import { formatTeamDeadline } from '../utils/teamDeadline';
import { labelSourceMarker } from '../utils/labelSource';
import { motion, useReducedMotion, type Variants, type Transition } from 'framer-motion';
import { type HistoryItem, type Job, type JobStatus, type ProductScanHistoryJob, type ScanType } from '../types/api';

// Numeric mirrors of --t-fast (180ms) for framer-motion
const T_FAST = 0.18;

const FACE_TYPES: ScanType[] = ['base', 'premium', 'hair', 'makeup'];
const isProductType = (t: ScanType) => t === 'product' || t === 'comparison' || t === 'compatibility';

type HistoryTab = 'face' | 'products';
type ProductSubtype = 'all' | 'single' | 'comparison' | 'compatibility';

// Maps a Products sub-filter to the matching history job_type ('single' tile is
// stored as job_type 'product').
const subtypeMatches = (jobType: ScanType, subtype: ProductSubtype) =>
  subtype === 'all' ||
  (subtype === 'single' ? jobType === 'product' : jobType === subtype);

function TypeBadge({ type }: { type: ScanType }) {
  const map: Record<ScanType, string> = {
    base: 'pill',
    premium: 'pill pill--teal',
    hair: 'pill pill--teal',
    makeup: 'pill pill--coral',
    product: 'pill',
    comparison: 'pill pill--teal',
    compatibility: 'pill pill--coral',
  };
  const labels: Record<ScanType, string> = {
    base: 'Base',
    premium: 'Premium',
    hair: 'Hair',
    makeup: 'Makeup',
    product: 'Product',
    comparison: 'Comparison',
    compatibility: 'Compatibility',
  };
  const coralDot = type === 'makeup' || type === 'compatibility';
  return (
    <span className={map[type]}>
      <span className={coralDot ? 'dot dot--coral' : 'dot'} />
      {labels[type]}
    </span>
  );
}

function SkinTypeBadge({ type }: { type: 'Normal' | 'Oily' | 'Dry' }) {
  return (
    <span className="pill">
      <span className="dot dot--coral" />
      {type} skin
    </span>
  );
}

function StatusChip({ status }: { status: JobStatus }) {
  const clsMap: Record<JobStatus, string> = {
    queued:     'pill',
    processing: 'pill pill--teal',
    done:       'pill pill--teal',
    error:      'pill pill--coral',
  };
  const labelMap: Record<JobStatus, string> = {
    queued: 'Queued', processing: 'Processing', done: 'Done', error: 'Error',
  };
  return (
    <span className={clsMap[status]}>
      {labelMap[status]}
    </span>
  );
}

/** A side whose ingredient list the team is still writing (no analysis yet). */
const isAwaitingTeam = (job: ProductScanHistoryJob) =>
  job.ingredients_state_a === 'awaiting_team' || job.ingredients_state_b === 'awaiting_team';

/** "Checking ingredients", plus "· by Oct 21" when the team deadline is known. */
function checkingLabel(job: ProductScanHistoryJob): string {
  const iso = job.ingredients_state_a === 'awaiting_team' ? job.team_deadline_a : job.team_deadline_b;
  const by = formatTeamDeadline(iso);
  return by ? `Checking ingredients · by ${by}` : 'Checking ingredients';
}

// "BrandA Name" — falls back gracefully when brand/name are missing.
const productLabel = (brand?: string, name?: string) =>
  [brand, name].filter(Boolean).join(' ').trim() || 'Product';

const productEntryTitle = (job: ProductScanHistoryJob, type: ScanType) => {
  const a = productLabel(job.brand_a, job.name_a);
  if (type === 'product') return a;
  const b = productLabel(job.brand_b, job.name_b);
  return `${a} ${type === 'comparison' ? 'vs' : '+'} ${b}`;
};

function getSkinType(job: Job): 'Normal' | 'Oily' | 'Dry' {
  const scores = { normal: job.skin_normal ?? 0, dry: job.skin_dry ?? 0, oily: job.skin_oily ?? 0 };
  const top = (Object.keys(scores) as (keyof typeof scores)[]).reduce((a, b) =>
    scores[a] >= scores[b] ? a : b,
  );
  return (top.charAt(0).toUpperCase() + top.slice(1)) as 'Normal' | 'Oily' | 'Dry';
}

export default function HistoryPage(): React.JSX.Element {
  const [items, setItems] = useState<HistoryItem[]>();
  const [tab, setTab] = useState<HistoryTab>('face');
  const [subtype, setSubtype] = useState<ProductSubtype>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | JobStatus>('all');
  const [showErrModal, setShowErrModal] = useState<{ open: boolean; jobId?: string; jobType?: ScanType }>({ open: false });
  const [dismissing, setDismissing] = useState(false);

  const prefersReduced = useReducedMotion();
  const spring: Transition = prefersReduced
    ? { duration: 0 }
    : { type: 'spring', stiffness: 380, damping: 34, mass: 0.95 };
  const pageIn: Variants = {
    hidden: { opacity: 0, y: 8 },
    visible: { opacity: 1, y: 0, transition: { duration: prefersReduced ? 0 : T_FAST } },
  };
  const listContainer: Variants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: prefersReduced ? 0 : 0.06,
        delayChildren:   prefersReduced ? 0 : 0.04,
      },
    },
  };
  const listItem: Variants = {
    hidden: { opacity: 0, y: 6 },
    visible: { opacity: 1, y: 0, transition: spring },
  };

  // Fetch per tab so each tab shows its own items regardless of the other's
  // volume (face scans and product scans share one history feed/limit).
  useEffect(() => {
    let alive = true;
    setItems(undefined);
    void getHistory({ type: tab === 'products' ? 'product' : 'face', limit: 50 })
      .then((res) => { if (alive) setItems(res.items); });
    return () => { alive = false; };
  }, [tab]);

  const filtered = useMemo(() => {
    if (!items) return;
    if (tab === 'products') {
      return items.filter((i) =>
        isProductType(i.job_type) && subtypeMatches(i.job_type, subtype),
      );
    }
    return items.filter((i) =>
      FACE_TYPES.includes(i.job_type) && (statusFilter === 'all' || i.job.status === statusFilter),
    );
  }, [items, tab, subtype, statusFilter]);

  if (!filtered) return <Loading />;

  const prefixFor = (job_type: ScanType) => {
    if (job_type === 'premium') return '/premium-analysis';
    if (job_type === 'hair') return '/hair-analysis';
    if (job_type === 'makeup') return '/makeup-analysis';
    return '/analyses';
  };

  // Re-open a product scan: navigate to its result page with the scan encoded in
  // the URL (barcode(s) + product id(s), plus context/order for compatibility).
  // The result page re-runs the analysis, which is free for an already-scanned
  // product/pair.
  const openProductScan = (i: HistoryItem) => {
    const job = i.job as ProductScanHistoryJob;

    // Single product scan → the single-product result page (barcode-aware so it
    // doesn't prompt for confirmation; '' means a barcode-less slug product).
    // A private label submission has no barcode of its own: the stored one is
    // only the barcode it was claimed under, so open it by id alone.
    if (i.job_type === 'product') {
      const barcode = job.visibility_a === 'owner_only' ? '' : job.barcode_a ?? '';
      const q = new URLSearchParams({ barcode });
      navigateTo(`/scanner/result/${job.product_id_a}?${q.toString()}`);
      return;
    }

    const base = i.job_type === 'comparison' ? '/comparison/result' : '/compatibility/result';
    const q = new URLSearchParams();
    if (job.barcode_a) q.set('a', job.barcode_a);
    if (job.barcode_b) q.set('b', job.barcode_b);
    q.set('pa', String(job.product_id_a));
    q.set('pb', String(job.product_id_b));
    if (i.job_type === 'compatibility') {
      if (job.context) q.set('context', job.context);
      if (job.order) q.set('order', job.order);
    }
    navigateTo(`${base}?${q.toString()}`);
  };

  const handleItemClick = (i: HistoryItem) => {
    if (isProductType(i.job_type)) { openProductScan(i); return; }

    const { job, job_type } = i;
    if (job.status === 'error') { setShowErrModal({ open: true, jobId: job.id, jobType: job_type }); return; }
    const prefix = prefixFor(job_type);

    if (job.status === 'queued' || job.status === 'processing') {
      navigateTo(`${prefix}/${job.id}/pending`);
      return;
    }
    navigateTo(`${prefix}/${job.id}`);
  };

  const handleDismissError = async () => {
    if (!showErrModal.jobId || !showErrModal.jobType) return;
    setDismissing(true);
    try {
      const { jobId, jobType } = showErrModal;
      if (jobType === 'hair') await cancelHairAnalysisJob(jobId);
      else if (jobType === 'makeup') await cancelMakeupAnalysisJob(jobId);
      else if (jobType === 'premium') await cancelPremiumJob(jobId);
      else await cancelJob(jobId);
      setItems((prev) => prev?.filter((i) => i.job.id !== jobId));
      setShowErrModal({ open: false });
    } catch {
      // silent — just close the modal if delete fails
      setShowErrModal({ open: false });
    } finally {
      setDismissing(false);
    }
  };

  const emptyText =
    (items?.length ?? 0) === 0 ? 'No history yet' : 'No results for this filter';

  return (
    <motion.div initial="hidden" animate="visible" variants={pageIn} className="stack-5">

      {/* ── Header ──────────────────────────────────────────── */}
      <div className="row-4" style={{ justifyContent: 'space-between' }}>
        <h1 className="t-h2" style={{ margin: 0 }}>History</h1>

        {/* Status filter — only meaningful for face scans (product scans are
            always complete). */}
        {tab === 'face' && (
          <div style={{ position: 'relative' }}>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as 'all' | JobStatus); }}
              className="field"
              style={{ paddingRight: 'var(--s-7)', appearance: 'none', minWidth: 120 }}
              aria-label="Filter by status"
            >
              <option value="all">All</option>
              <option value="queued">Queued</option>
              <option value="processing">Processing</option>
              <option value="done">Done</option>
              <option value="error">Error</option>
            </select>
            <svg
              style={{
                position: 'absolute', right: 'var(--s-3)', top: '50%',
                transform: 'translateY(-50%)', pointerEvents: 'none',
                color: 'var(--ink-muted)',
              }}
              width={16} height={16}
              viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
        )}
      </div>

      {/* ── Tabs: Face / Products ─────────────────────────────── */}
      <div
        className="row-2"
        role="tablist"
        aria-label="History type"
        style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0,
          borderRadius: 'var(--r-sm)', border: '1px solid var(--rule)', overflow: 'hidden',
        }}
      >
        {(['face', 'products'] as HistoryTab[]).map((t, i) => (
          <button
            key={t}
            type="button"
            role="tab"
            aria-selected={tab === t}
            onClick={() => { setTab(t); }}
            style={{
              padding: 'var(--s-2) var(--s-3)',
              borderLeft: i > 0 ? '1px solid var(--rule)' : undefined,
              background: tab === t ? 'var(--c-kivo-teal)' : 'var(--c-cream)',
              color: tab === t ? 'var(--c-cream)' : 'var(--ink)',
              transition: 'background var(--t-fast) var(--ease)',
              cursor: 'pointer', border: 'none', font: 'inherit',
            }}
          >
            {t === 'face' ? 'Face scans' : 'Products'}
          </button>
        ))}
      </div>

      {/* ── Product subtype chips ─────────────────────────────── */}
      {tab === 'products' && (
        <div className="row-3" role="group" aria-label="Product scan type" style={{ flexWrap: 'wrap' }}>
          {(['all', 'single', 'comparison', 'compatibility'] as ProductSubtype[]).map((s) => {
            const active = subtype === s;
            const label =
              s === 'all' ? 'All'
              : s === 'single' ? 'Single'
              : s === 'comparison' ? 'Comparison'
              : 'Compatibility';
            return (
              <button
                key={s}
                type="button"
                aria-pressed={active}
                onClick={() => { setSubtype(s); }}
                className="pill"
                style={{
                  cursor: 'pointer', border: '1px solid var(--rule)',
                  background: active ? 'var(--c-kivo-teal)' : 'transparent',
                  color: active ? 'var(--c-cream)' : 'var(--ink)',
                }}
              >
                {label}
              </button>
            );
          })}
        </div>
      )}

      {/* ── List card ─────────────────────────────────────────── */}
      <motion.div
        variants={pageIn}
        className="card card--bordered card--flat"
        style={{ padding: 'var(--s-2)', background: 'var(--c-cream)' }}
      >
        {filtered.length === 0 ? (
          <p
            className="t-mute"
            style={{ padding: 'var(--s-5) 0', textAlign: 'center', margin: 0 }}
          >
            {emptyText}
          </p>
        ) : (
          <motion.ul
            initial="hidden"
            animate="visible"
            variants={listContainer}
            className="hist-list"
          >
            {filtered.map((item, i) => {
              const { job, job_type } = item;
              const product = isProductType(job_type) ? (job as ProductScanHistoryJob) : null;
              const isDone = job.status === 'done';
              const skinType = isDone && job_type === 'base' ? getSkinType(job as Job) : null;

              return (
                <motion.li
                  key={job.id}
                  variants={listItem}
                  whileHover={prefersReduced ? {} : { y: -1 }}
                  transition={spring}
                >
                  <button
                    type="button"
                    onClick={() => { handleItemClick(item); }}
                    className={`hist-row${job.status === 'error' ? ' hist-row--error' : ''}`}
                    aria-label={`${job_type} from ${new Date(job.submitted_at).toLocaleString()}`}
                  >
                    {/* Left: thumbnail + badge stack */}
                    <div className="row-4" style={{ flex: 1, minWidth: 0 }}>
                      <motion.div
                        initial={prefersReduced ? {} : { scale: 0.98, opacity: 0 }}
                        animate={prefersReduced ? {} : { scale: 1, opacity: 1 }}
                        transition={spring}
                        style={{ display: 'flex' }}
                      >
                        {product ? (
                          <ProductPairThumbnail
                            single={job_type === 'product'}
                            imageUrlA={product.image_url_a}
                            imageUrlB={product.image_url_b}
                            alt={productEntryTitle(product, job_type)}
                          />
                        ) : (
                          <div className="hist-thumb">
                            <AuthImage path={(job as Job).image_path} lazy alt={job.id} />
                          </div>
                        )}
                      </motion.div>

                      <div className="stack-2" style={{ minWidth: 0 }}>
                        <div className="row-3" style={{ flexWrap: 'wrap' }}>
                          <TypeBadge type={job_type} />
                          {isDone && skinType && <SkinTypeBadge type={skinType} />}
                          {!isDone && <StatusChip status={job.status} />}
                          {product && (labelSourceMarker(product.label_source_a) ?? labelSourceMarker(product.label_source_b)) && (
                            <span className="pill">{labelSourceMarker(product.label_source_a) ?? labelSourceMarker(product.label_source_b)}</span>
                          )}
                          {product && isAwaitingTeam(product) && (
                            <span className="pill pill--coral">{checkingLabel(product)}</span>
                          )}
                        </div>
                        {product && (
                          <span className="t-small" style={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {productEntryTitle(product, job_type)}
                          </span>
                        )}
                        <time className="t-small t-mute">
                          {new Date(job.submitted_at).toLocaleString()}
                        </time>
                      </div>
                    </div>

                    {/* Right: CTA label + chevron */}
                    <div className={`hist-cta${job.status === 'error' ? ' hist-cta--error' : ''}`}>
                      <span className="t-small">
                        {job.status === 'error'
                          ? 'Error'
                          : product
                          ? 'View result'
                          : job.status === 'done'
                          ? 'Learn more'
                          : 'View status'}
                      </span>
                      {job.status !== 'error' && (
                        <motion.svg
                          width={18} height={18}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                          initial={prefersReduced ? {} : { x: -2, opacity: 0 }}
                          animate={prefersReduced ? {} : { x: 0, opacity: 1 }}
                          transition={spring}
                        >
                          <path d="M9 6l6 6-6 6" />
                        </motion.svg>
                      )}
                    </div>
                  </button>
                  {i < filtered.length - 1 && (
                    <div className="hist-separator" aria-hidden="true" />
                  )}
                </motion.li>
              );
            })}
          </motion.ul>
        )}
      </motion.div>

      {/* ── Error modal (portaled to <body> so it centers on the screen, ── */}
      {/*    not the scrolled content) ──────────────────────────────── */}
      {showErrModal.open && typeof document !== 'undefined' && ReactDOM.createPortal(
        <div className="modal">
          <div
            className="modal__backdrop"
            role="button"
            tabIndex={0}
            aria-label="Close dialog"
            onClick={() => { setShowErrModal({ open: false }); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowErrModal({ open: false });
              }
            }}
          />
          <div className="modal__card">
            <p className="modal__title">This analysis encountered an error</p>
            <p className="modal__body">
              Something went wrong during processing. Please try again with a new photo.
            </p>
            <div className="modal__actions">
              <button
                className="btn btn--ghost btn--sm"
                onClick={() => { setShowErrModal({ open: false }); }}
              >
                Close
              </button>
              <button
                className="btn btn--ghost btn--sm"
                style={{ color: 'var(--c-coral)' }}
                onClick={handleDismissError}
                disabled={dismissing}
              >
                {dismissing ? 'Removing…' : 'Dismiss'}
              </button>
              <button
                className="btn btn--primary btn--sm"
                style={{ marginLeft: 'auto' }}
                onClick={() => { navigateTo('/new'); }}
              >
                Start new analysis
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

    </motion.div>
  );
}
